import { useCssVars as _useCssVars, defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "desc" }
const _hoisted_2 = { class: "header" }
const _hoisted_3 = { class: "edit-slide-down" }
const _hoisted_4 = { class: "edit-slide-down__control" }
const _hoisted_5 = { class: "edit-slide-down__control__info" }
const _hoisted_6 = { class: "edit-slide-down__control__info--num" }

import { i18n } from '@/i18n';
import EditPageLayout from './EditPageLayout.vue';
import { computed, nextTick, onMounted, ref } from 'vue';
import FilterModalButton from '@/corePagesModule/filterModal/FilterModalButton.vue';
import Pagination from '@/corePagesModule/components/Pagination.vue';
import EzTable from '@/corePagesModule/components/ezTable/EzTable.vue';
import EzHeader from '@/corePagesModule/components/ezTable/EzHeader.vue';
import { useTableHeader } from '@/corePagesModule/composables/useTableHeader';
import { MODE_CONFIG } from '../constants';
import EzTableRow from '@/corePagesModule/components/ezTable/EzTableRow.vue';
import EzCell from '@/corePagesModule/components/ezTable/EzCell.vue';
import { EditBusinessViewModel } from './editBusinessViewModel';
import TableHeaderModifyButton from '@/corePagesModule/components/TableHeaderModifyButton.vue';
import { HeaderList } from '@/coreDomainsModule/table';
import TableCheckbox from '../components/TableCheckbox.vue';
import SegmentControl from '@/corePagesModule/components/SegmentControl.vue';
import { EditBusinessMode } from './useEditBusinessStore/types';
import { useBulkEditBusinessStore } from './useEditBusinessStore';
import { storeToRefs } from 'pinia';
import { FilterContentItem } from '@/corePagesModule/filterModal/filterModal';
import TextButton from '@/components/input/Btn/TextButton.vue';
import { initPagingOptions, IPagingOptions } from '@/coreDomainsModule/paging';
import TopSlideDownModal from '@/corePagesModule/components/TopSlideDownModal.vue';
import UpdateBaseBusinessWideSection from '../components/UpdateBaseBusinessWideSection.vue';
import UpdateAggregateBusinessWideSection from '../components/UpdateAggregateBusinessWideSection.vue';
import { waitLoading } from '@/corePagesModule/components/InfoModal/waitLoading';
import { toast } from '@/corePagesModule/components/Toast/ToastManager';
import { ErrorResponseCode } from '@/coreInfrastructureModule/axios/generated/aggregateBusinessManage/api';
import { diContainer } from '@/coreContainer/container';
import { IBusiness } from '@/coreDomainsModule/entities/businessBase';
import { showInfoModal } from '@/corePagesModule/components/InfoModal/InfoModalManager';
import { useColumnResizer } from '@/corePagesModule/composables/useColumnResizer';
import { useColumnWidth } from '@/corePagesModule/composables/useColumnWidth';

// #region Component constants
const SLIDE_DOWN_HEIGHT = 280;
const SLIDE_DOWN_TABLE_GAP = 8;
const SLIDE_DOWN_ANIMATION_DURATION_MS = 300;
const TABLE_RESIZE_ANIMATION_DURATION_MS = 120;
const TABLE_VIEWPORT_BOTTOM_MARGIN = 16;
const SLIDE_DOWN_Z = 900;
type TableSlideAnimationState = {
  fixedTop: string;
  fixedLeft: string;
  fixedRight: string;
  fixedHeight: string;
  finalHeight: string;
  pushDistance: string;
  pushDelay: string;
  pushDuration: string;
  resizeDelay: string;
};


export default /*@__PURE__*/_defineComponent({
  __name: 'BulkEditBusinessPage',
  setup(__props) {

_useCssVars(_ctx => ({
  "51044eb4": (tableSlideAnimationState.value.fixedTop),
  "210c73bb": (tableSlideAnimationState.value.fixedRight),
  "60fc82f0": (tableSlideAnimationState.value.fixedLeft),
  "218bd930": (tableSlideAnimationState.value.fixedHeight),
  "c5b3727e": (tableSlideAnimationState.value.pushDuration),
  "73a41776": (tableSlideAnimationState.value.pushDelay),
  "378cbe65": (`${TABLE_RESIZE_ANIMATION_DURATION_MS}ms`),
  "e323da48": (tableSlideAnimationState.value.resizeDelay),
  "25138522": (tableSlideAnimationState.value.pushDistance),
  "2c3ae3ea": (tableSlideAnimationState.value.finalHeight)
}))

const t = i18n.t;
const vm = new EditBusinessViewModel();
const store = useBulkEditBusinessStore();
const { state } = storeToRefs(store);
const { init, fetchEditBusiness, additionalValidate } = store;
// #endregion

/**
 * テーブル追従アニメーションで使用するCSS値
 */
const DEFAULT_TABLE_SLIDE_ANIMATION_STATE: TableSlideAnimationState = {
  fixedTop: '0px',
  fixedLeft: '0px',
  fixedRight: '0px',
  fixedHeight: '0px',
  finalHeight: '0px',
  pushDistance: '0px',
  pushDelay: '0s',
  pushDuration: `${SLIDE_DOWN_ANIMATION_DURATION_MS}ms`,
  resizeDelay: `${SLIDE_DOWN_ANIMATION_DURATION_MS}ms`,
};

/**
 * 数値をpx単位のCSS値へ変換する
 * @param value - 変換する数値
 */
const toPx = (value: number) => `${value}px`;

/**
 * 数値をms単位のCSS値へ変換する
 * @param value - 変換する数値
 */
const toMs = (value: number) => `${value}ms`;

// #region Refs
const updateBaseBusinessWideSectionRef = ref<InstanceType<typeof UpdateBaseBusinessWideSection> | null>(null);
const updateAggregateBusinessWideSectionRef = ref<InstanceType<typeof UpdateAggregateBusinessWideSection> | null>(null);
const tableContainerRef = ref<HTMLElement | null>(null);
const tableRef = ref<HTMLElement | null>(null);
const tableSlideAnimationState = ref<TableSlideAnimationState>(DEFAULT_TABLE_SLIDE_ANIMATION_STATE);
const tableWidthInfo = ref<Record<string, number | undefined>>({});
// #endregion

// #region Computed refs
const pageTitle = computed(() => `${t('取引一括編集')}`);
const modeConfig = computed(() => MODE_CONFIG[state.value.businessMode]);
const tableType = computed(() => modeConfig.value.tableType.bulk);
const businessFreeColumnHeaderOptions = computed(() => {
  const headers = new Map<number, string>();
  for (const business of state.value.dataList) {
    for (const column of business.businessFreeColumnValues ?? []) {
      if (column.isConfigured) headers.set(column.columnNo, column.headerName);
    }
  }
  return [...headers.entries()]
    .sort(([a], [b]) => a - b)
    .map(([columnNo, headerName]) => ({ key: `businessFreeColumnValues-${columnNo}`, label: headerName, type: 'str' as const }));
});
const fullHeaderOptions = computed(() => {
  const trailingHeaderKeys = ['createdAt', 'updatedAt', 'headerModify'];
  const trailingHeaderOptions = trailingHeaderKeys
    .map((key) => modeConfig.value.headerOptions.find((h) => h.key === key))
    .filter((h): h is NonNullable<typeof h> => h !== undefined);

  return [
    ...modeConfig.value.headerOptions.filter((h) => !trailingHeaderKeys.includes(h.key)),
    ...businessFreeColumnHeaderOptions.value,
    ...trailingHeaderOptions,
  ];
});
const fullHeaderKeys = computed(() => fullHeaderOptions.value.map((h) => h.key));

const { headerList, updateDisplayHeader } = useTableHeader(fullHeaderKeys, tableType, []);
useColumnWidth({ tableType: tableType, widthInfo: tableWidthInfo });
const { onMousedownResizer } = useColumnResizer({
  tableType: tableType,
  widthInfo: tableWidthInfo,
  tableEl: tableRef,
});

const headerOptions = computed(() => fullHeaderOptions.value.filter((h) => headerList.value.includes(h.key)));
const headerWidths = computed(() => headerOptions.value.map((h) => `${vm.getHeaderWidth(h, tableWidthInfo.value)}px`));
const ModifiableHeaderKeys = computed(
  (): HeaderList =>
    fullHeaderOptions.value
      .filter((h) => !['checkbox', 'headerModify'].includes(h.key))
      .map((h) => ({
        key: h.key,
        text: h.label,
        isShown: headerList.value.includes(h.key),
      }))
);
const dataListBusinessIds = computed(() => state.value.dataList.map((d) => d.businessId));
const checkedCount = computed(() => state.value.checkedIds.size);
const isAllChecked = computed(() => !!state.value.dataList.length && checkedCount.value === state.value.dataList.length);
const isIndeterminate = computed(() => !!checkedCount.value && !isAllChecked.value);
const updateSectionRefWithMode = computed(() =>
  state.value.businessMode === 'baseBusiness' ? updateBaseBusinessWideSectionRef.value : updateAggregateBusinessWideSectionRef.value
);
// #endregion

// #region Functions
/**
 * 取引タイプを切り替えた際の処理
 * @param businessMode - 取引タイプ
 */
const changeBusinessMode = (businessMode: EditBusinessMode) => {
  const pageSize = state.value.pagingOptions.pageSize;
  init(async () => {
    state.value.businessMode = businessMode;
    state.value.pagingOptions = initPagingOptions(pageSize);
  });
};

/**
 * ヘッダーのチェックボックスを押した際のハンドラ
 * @param isChecked - チェックボックスの状態
 */
const changeHeaderCheckbox = (isChecked: boolean) => {
  if (isChecked) {
    const next = new Set(dataListBusinessIds.value);
    state.value.checkedIds = next;
  } else {
    state.value.checkedIds = new Set();
  }
};

/**
 * 行のチェックボックスを押した際のハンドラ
 * @param businessId - 当該行取引ID
 * @param isChecked - チェックボックスの状態
 */
const changeCheckbox = (businessId: string, isChecked: boolean) => {
  const next = new Set(state.value.checkedIds);
  if (isChecked) {
    next.add(businessId);
  } else {
    next.delete(businessId);
  }
  state.value.checkedIds = next;
};

/**
 * フィルターを適用した際の処理
 * @param filterItems - フィルターアイテム
 */
const applyFilter = async (filterItems: FilterContentItem[]) => {
  state.value.filterItems = filterItems;
  await fetchEditBusiness();
};

/**
 * ページング変更時の処理
 * @param pagingOptions - ページングオプション
 */
const changePaging = async (pagingOptions: IPagingOptions) => {
  state.value.pagingOptions = pagingOptions;
  await fetchEditBusiness();
};

/**
 * スライドダウンがテーブルに到達したタイミングで押し下げるためのアニメーション値を作成する
 * @param tableRect - ドロップダウンを開く直前のテーブル表示領域
 */
const createTableSlideAnimationState = (tableRect: DOMRect): TableSlideAnimationState => {
  const finalTableTop = SLIDE_DOWN_HEIGHT + SLIDE_DOWN_TABLE_GAP;
  const collisionDistance = Math.max(tableRect.top - SLIDE_DOWN_TABLE_GAP, 0);
  const pushDelayMs = Math.min(
    (collisionDistance / SLIDE_DOWN_HEIGHT) * SLIDE_DOWN_ANIMATION_DURATION_MS,
    SLIDE_DOWN_ANIMATION_DURATION_MS
  );
  const pushDurationMs = Math.max(SLIDE_DOWN_ANIMATION_DURATION_MS - pushDelayMs, 0);
  const finalTableHeight = Math.max(window.innerHeight - finalTableTop - TABLE_VIEWPORT_BOTTOM_MARGIN, 0);

  return {
    fixedTop: toPx(tableRect.top),
    fixedLeft: toPx(tableRect.left),
    fixedRight: toPx(window.innerWidth - tableRect.right),
    fixedHeight: toPx(tableRect.height),
    finalHeight: toPx(finalTableHeight),
    pushDistance: toPx(finalTableTop - tableRect.top),
    pushDelay: toMs(pushDelayMs),
    pushDuration: toMs(pushDurationMs),
    resizeDelay: toMs(pushDelayMs + pushDurationMs),
  };
};

/**
 * テーブルをスライドダウンの動きに追従させるための値を更新する
 */
const prepareTableSlideAnimation = () => {
  const tableRect = tableContainerRef.value?.getBoundingClientRect();
  if (!tableRect) return;

  tableSlideAnimationState.value = createTableSlideAnimationState(tableRect);
};

/**
 * ドロップダウンを開く
 */
const openDropdown = () => {
  prepareTableSlideAnimation();
  state.value.editDropdownState = true;
};

/**
 * ドロップダウンを再表示する
 * @param targetInfo - 対象データ
 */
const reShowDropdown = (targetInfo: Partial<IBusiness>) => {
  state.value.editDropdownState = true;
  nextTick(() => {
    if (!updateSectionRefWithMode.value) return;
    updateSectionRefWithMode.value.initializeForm(targetInfo);
    updateSectionRefWithMode.value.isEdited = true;
  });
};

/**
 * 共通エラーモーダルを出す
 */
const showCommonErrorModal = () =>
  showInfoModal({
    modalTitle: t('エラーが発生しました'),
    modalDescription: t('更新中にエラーが発生しました。時間をおいて再度お試しください。'),
    isDisabledNextButton: true,
    backButtonLabel: t('OK'),
    icon: 'error',
    colorStyle: 'error',
  });

/**
 * 保存ボタン押下
 */
const onSave = async () => {
  if (!updateSectionRefWithMode.value) return;
  const commonValidateResult = updateSectionRefWithMode.value?.allValidate();

  const additionalInvalidItems = additionalValidate(updateSectionRefWithMode.value?.fieldState);
  for (const item of additionalInvalidItems) {
    const targetState = updateSectionRefWithMode.value?.fieldState[item];
    if (!targetState) continue;
    targetState.isError.value = true;
    targetState.errorMessage.value = t('入力してください');
  }
  await nextTick();
  if (!commonValidateResult || additionalInvalidItems.length) return;

  state.value.editDropdownState = false;
  const targetInfo =
    state.value.businessMode === 'baseBusiness'
      ? updateBaseBusinessWideSectionRef.value?.createBaseBusinessPatch()
      : updateAggregateBusinessWideSectionRef.value?.createAggregateBusinessPatch();
  if (!targetInfo) {
    await showCommonErrorModal();
    return;
  }
  const nullableFormState = updateSectionRefWithMode.value.nullableFormState;
  const nullKeys = Object.keys(nullableFormState).filter((k) => nullableFormState[k]);
  const useCase =
    state.value.businessMode === 'baseBusiness' ? diContainer.bulkPutBaseBusinessUseCase : diContainer.bulkPutAggregateBusinessUseCase;
  const res = await waitLoading(useCase.execute([...state.value.checkedIds], targetInfo, nullKeys));
  if (res?.status.type === 'success') {
    state.value.loadingStatus.type = 'loading';
    fetchEditBusiness();
    toast.success('更新に成功しました');
  } else {
    if (res?.status.error.code === ErrorResponseCode.ResourceConflict) {
      await showInfoModal({
        modalTitle: t('エラーが発生しました'),
        modalDescription: t('一括変更後の取引が既存の取引と重複しています。既存の取引を確認してください。'),
        isDisabledNextButton: true,
        backButtonLabel: t('OK'),
        icon: 'error',
        colorStyle: 'error',
      });
    } else {
      await showCommonErrorModal();
    }
    reShowDropdown(targetInfo);
  }
};

/**
 * ハイライトされているかどうか
 * @param businessId - 取引ID
 */
const getIsChecked = (businessId: string) => state.value.checkedIds.has(businessId);

// #endregion

// #region Event callbacks
onMounted(async () => {
  await init();
});
// #endregion

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(EditPageLayout, { title: pageTitle.value }, {
      default: _withCtx(() => [
        _createElementVNode("div", {
          class: _normalizeClass(["main", { 'main--slide-open': _unref(state).editDropdownState }])
        }, [
          _createElementVNode("div", _hoisted_1, _toDisplayString(_unref(t)('取引を選択して一括で項目の値を変更できます。')), 1),
          _createElementVNode("div", _hoisted_2, [
            _createVNode(SegmentControl, {
              "model-value": _unref(state).businessMode,
              "value-a": "aggregateBusiness",
              "label-a": _unref(t)('仕入取引'),
              "value-b": "baseBusiness",
              "label-b": _unref(t)('販売取引'),
              size: "sm",
              "onUpdate:modelValue": changeBusinessMode
            }, null, 8, ["model-value", "label-a", "label-b"]),
            _createVNode(TextButton, { onClick: openDropdown }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_unref(t)('一括編集')), 1)
              ], undefined, true),
              _: 1
            }),
            _createVNode(FilterModalButton, {
              "filter-modal-type": "inventory",
              "filter-items": _unref(state).filterItems,
              onApply: applyFilter
            }, null, 8, ["filter-items"]),
            _createVNode(Pagination, {
              "paging-options": _unref(state).pagingOptions,
              "is-loading": _unref(state).loadingStatus.type === 'loading',
              "no-bottom-margin": "",
              class: "header__paging",
              onOnChangePage: changePaging
            }, null, 8, ["paging-options", "is-loading"])
          ]),
          _createElementVNode("div", {
            ref_key: "tableContainerRef",
            ref: tableContainerRef,
            class: "table-container"
          }, [
            _createVNode(EzTable, {
              ref_key: "tableRef",
              ref: tableRef,
              "grid-template-columns": headerWidths.value,
              "loading-status": _unref(state).loadingStatus,
              class: "list-table"
            }, {
              header: _withCtx(() => [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(headerOptions.value, (header) => {
                  return (_openBlock(), _createElementBlock(_Fragment, {
                    key: `header-${header.key}`
                  }, [
                    (header.key === 'checkbox')
                      ? (_openBlock(), _createBlock(EzHeader, {
                          key: 0,
                          sticky: "left",
                          class: "list-table__header"
                        }, {
                          default: _withCtx(() => [
                            _createVNode(TableCheckbox, {
                              "is-checked": isAllChecked.value,
                              "is-indeterminate": isIndeterminate.value,
                              onChange: changeHeaderCheckbox
                            }, null, 8, ["is-checked", "is-indeterminate"])
                          ], undefined, true),
                          _: 1
                        }))
                      : (header.key === 'headerModify')
                        ? (_openBlock(), _createBlock(EzHeader, {
                            key: 1,
                            sticky: "right",
                            class: "list-table__header"
                          }, {
                            default: _withCtx(() => [
                              _createVNode(TableHeaderModifyButton, {
                                "modifiable-header-list": ModifiableHeaderKeys.value,
                                onToggle: _unref(updateDisplayHeader)
                              }, null, 8, ["modifiable-header-list", "onToggle"])
                            ], undefined, true),
                            _: 1
                          }))
                        : (_openBlock(), _createBlock(EzHeader, {
                            key: 2,
                            "use-resizer": "",
                            class: "list-table__header",
                            onResizer: (e) => _unref(onMousedownResizer)(e, header.key)
                          }, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(_unref(t)(header.label)), 1)
                            ], undefined, true),
                            _: 2
                          }, 1032, ["onResizer"]))
                  ], 64))
                }), 128))
              ]),
              body: _withCtx(() => [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(state).dataList, (data, index1) => {
                  return (_openBlock(), _createBlock(EzTableRow, {
                    key: `row-${index1}`,
                    "grid-template-columns": headerWidths.value
                  }, {
                    default: _withCtx(() => [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(headerOptions.value, (header) => {
                        return (_openBlock(), _createElementBlock(_Fragment, {
                          key: `row-${index1}-${header.key}`
                        }, [
                          (header.key === 'checkbox')
                            ? (_openBlock(), _createBlock(EzCell, {
                                key: 0,
                                sticky: "left",
                                "line-height": 24,
                                "is-highlighted": getIsChecked(data.businessId),
                                class: "list-table__cell"
                              }, {
                                default: _withCtx(() => [
                                  _createVNode(TableCheckbox, {
                                    "is-checked": getIsChecked(data.businessId),
                                    onChange: (isChecked) => changeCheckbox(data.businessId, isChecked)
                                  }, null, 8, ["is-checked", "onChange"])
                                ], undefined, true),
                                _: 2
                              }, 1032, ["is-highlighted"]))
                            : (header.key === 'headerModify')
                              ? (_openBlock(), _createBlock(EzCell, {
                                  key: 1,
                                  "line-height": 24,
                                  "is-highlighted": getIsChecked(data.businessId),
                                  class: "list-table__cell"
                                }, null, 8, ["is-highlighted"]))
                              : (_openBlock(), _createBlock(EzCell, {
                                  key: 2,
                                  "is-number": header.type === 'num',
                                  "line-height": 24,
                                  "is-highlighted": getIsChecked(data.businessId),
                                  class: _normalizeClass(["list-table__cell", {
                    'list-table__cell--edited':
                      _unref(state).checkedIds.has(data.businessId) &&
                      _unref(vm).hasEditedDiff(
                        header.key,
                        data,
                        updateSectionRefWithMode.value?.fieldState,
                        updateSectionRefWithMode.value?.nullableFormState,
                        updateSectionRefWithMode.value?.businessFreeColumnValues
                      ),
                  }])
                                }, {
                                  default: _withCtx(() => [
                                    _createTextVNode(_toDisplayString(_unref(vm).getDisplayData(header.key, data)) + " ", 1),
                                    (
                      _unref(state).checkedIds.has(data.businessId) &&
                      _unref(vm).hasEditedDiff(
                        header.key,
                        data,
                        updateSectionRefWithMode.value?.fieldState,
                        updateSectionRefWithMode.value?.nullableFormState,
                        updateSectionRefWithMode.value?.businessFreeColumnValues
                      )
                    )
                                      ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                          _createTextVNode(_toDisplayString(_unref(vm).getDisplayDiffData(
                        header.key,
                        updateSectionRefWithMode.value?.fieldState,
                        updateSectionRefWithMode.value?.nullableFormState,
                        updateSectionRefWithMode.value?.selectedUserAccounts,
                        updateSectionRefWithMode.value?.businessFreeColumnValues
                      )), 1)
                                        ], 64))
                                      : _createCommentVNode("", true)
                                  ], undefined, true),
                                  _: 2
                                }, 1032, ["is-number", "is-highlighted", "class"]))
                        ], 64))
                      }), 128))
                    ], undefined, true),
                    _: 2
                  }, 1032, ["grid-template-columns"]))
                }), 128))
              ]),
              _: 1
            }, 8, ["grid-template-columns", "loading-status"])
          ], 512)
        ], 2)
      ], undefined, true),
      _: 1
    }, 8, ["title"]),
    _createVNode(TopSlideDownModal, {
      "is-open": _unref(state).editDropdownState,
      height: SLIDE_DOWN_HEIGHT,
      "z-index": SLIDE_DOWN_Z
    }, {
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_3, [
          (_unref(state).businessMode === 'baseBusiness')
            ? (_openBlock(), _createBlock(UpdateBaseBusinessWideSection, {
                key: 0,
                ref_key: "updateBaseBusinessWideSectionRef",
                ref: updateBaseBusinessWideSectionRef
              }, null, 512))
            : (_unref(state).businessMode === 'aggregateBusiness')
              ? (_openBlock(), _createBlock(UpdateAggregateBusinessWideSection, {
                  key: 1,
                  ref_key: "updateAggregateBusinessWideSectionRef",
                  ref: updateAggregateBusinessWideSectionRef
                }, null, 512))
              : _createCommentVNode("", true),
          _createElementVNode("div", _hoisted_4, [
            _createElementVNode("div", _hoisted_5, [
              _createTextVNode(_toDisplayString(_unref(t)('選択中の取引')) + " ", 1),
              _createElementVNode("span", _hoisted_6, _toDisplayString(_unref(state).checkedIds.size), 1),
              _createTextVNode(" " + _toDisplayString(_unref(t)('件')), 1)
            ]),
            _createVNode(TextButton, {
              "button-style": "btn-secondary",
              onClick: _cache[0] || (_cache[0] = ($event: any) => (_unref(state).editDropdownState = false))
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_unref(t)('閉じる')), 1)
              ], undefined, true),
              _: 1
            }),
            _createVNode(TextButton, {
              disabled: !_unref(state).checkedIds.size || !updateSectionRefWithMode.value?.isEdited,
              onClick: onSave
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_unref(t)('更新する')), 1)
              ], undefined, true),
              _: 1
            }, 8, ["disabled"])
          ])
        ])
      ], undefined, true),
      _: 1
    }, 8, ["is-open"])
  ], 64))
}
}

})